import { useState } from 'react';
import type { ThemeKey } from '@/pages/home/themes';
import {
  CONTENT_INK,
  CONTENT_MUTED,
  CONTENT_BORDER,
  CONTENT_GREEN,
  accentOf,
} from '@/pages/home/contentTokens';
import { setApplyPrefill } from '@/utils/applyPrefill';

interface Option {
  value: string;
  label: string;
  sub: string;
  icon: string;
}

interface Question {
  id: string;
  title: string;
  helper: string;
  options: Option[];
}

const QUESTIONS: Question[] = [
  {
    id: 'employment',
    title: '지금 내 상태는?',
    helper: '고용 상태에 따라 지원 한도가 달라집니다.',
    options: [
      { value: 'jobseeker', label: '구직자', sub: '미취업 · 전액 무료', icon: 'ri-user-search-line' },
      { value: 'employed', label: '재직자', sub: '고용보험 가입', icon: 'ri-briefcase-4-line' },
      { value: 'graduate', label: '졸업예정자', sub: '2년제 이상', icon: 'ri-graduation-cap-line' },
    ],
  },
  {
    id: 'age',
    title: '연령대는?',
    helper: '청년 우대 여부를 판정합니다.',
    options: [
      { value: 'youth', label: '만 34세 이하', sub: '청년 특별지원', icon: 'ri-user-smile-line' },
      { value: 'senior', label: '만 35세 이상', sub: '일반 발급 대상', icon: 'ri-user-line' },
    ],
  },
  {
    id: 'history',
    title: '내일배움카드 발급 이력은?',
    helper: '잔액에 따라 실부담이 달라집니다.',
    options: [
      { value: 'first', label: '처음 발급', sub: '전액 지원 가능', icon: 'ri-add-circle-line' },
      { value: 'used', label: '발급받은 적 있음', sub: '잔액 확인 필요', icon: 'ri-history-line' },
    ],
  },
];

type Answers = Record<string, string>;

interface Verdict {
  tone: 'green' | 'amber';
  badge: string;
  headline: string;
  desc: string;
  note: string;
}

function getVerdict(a: Answers): Verdict {
  const noteUsed = '기존 카드의 잔액과 유효기간을 먼저 확인하세요.';
  const noteFirst = '신규 발급 시 즉시 훈련비 지원이 시작됩니다.';

  if (a.employment === 'jobseeker') {
    return {
      tone: 'green',
      badge: '판정 완료 · 지원 대상',
      headline: '국민내일배움카드 대상자입니다',
      desc:
        a.age === 'youth'
          ? '만 34세 이하 구직자 · 청년 특별지원까지 검토 가능해 실부담 0원 과정이 많습니다.'
          : '만 35세 이상 구직자도 발급 대상입니다. K-디지털 과정은 전액 국비로 지원됩니다.',
      note: a.history === 'used' ? noteUsed : noteFirst,
    };
  }

  if (a.employment === 'graduate') {
    return {
      tone: 'green',
      badge: '판정 완료 · 조건부 대상',
      headline: '졸업예정 청년 발급 대상입니다',
      desc: '졸업예정 청년은 국민내일배움카드 발급이 가능하며 정부 지원 80% 선에서 지원됩니다.',
      note: a.history === 'used' ? noteUsed : '졸업 전 신청하면 수료 후 바로 취업 활동이 가능합니다.',
    };
  }

  return {
    tone: 'amber',
    badge: '판정 완료 · 재직자 트랙',
    headline: '재직자 향상훈련 대상입니다',
    desc: '고용보험 가입 재직자는 향상훈련 과정을 지원받습니다. 자부담이 일부 발생할 수 있습니다.',
    note: a.history === 'used' ? noteUsed : '회사 교육비 지원과 병행하면 실부담을 줄일 수 있습니다.',
  };
}

const labelOf = (qid: string, value: string) => {
  const q = QUESTIONS.find((item) => item.id === qid);
  return q?.options.find((o) => o.value === value)?.label ?? value;
};

export default function QualificationSection({
  theme,
  onGoContact,
}: {
  theme: ThemeKey;
  onGoContact: () => void;
}) {
  const accent = accentOf(theme);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [locked, setLocked] = useState(false);
  const [attached, setAttached] = useState(false);

  const total = QUESTIONS.length;
  const done = step >= total;
  const current = QUESTIONS[step];
  const verdict = done ? getVerdict(answers) : null;

  const handleSelect = (id: string, value: string) => {
    if (locked) return;
    setLocked(true);
    setAnswers((prev) => ({ ...prev, [id]: value }));
    window.setTimeout(() => {
      setStep((s) => s + 1);
      setLocked(false);
    }, 260);
  };

  const reset = () => {
    setAnswers({});
    setStep(0);
    setLocked(false);
    setAttached(false);
  };

  const handleAttach = () => {
    if (!verdict) return;
    const summary = [
      '[자격 자가진단 결과]',
      `현재 상태: ${labelOf('employment', answers.employment)}`,
      `연령대: ${labelOf('age', answers.age)}`,
      `카드 발급 이력: ${labelOf('history', answers.history)}`,
      `판정: ${verdict.headline}`,
      '→ 자격 및 자부담 확인 후 신청을 진행하고 싶습니다.',
    ].join('\n');
    setApplyPrefill(summary);
    setAttached(true);
    window.setTimeout(() => setAttached(false), 2600);
    onGoContact();
  };

  const toneColor = verdict?.tone === 'amber' ? '#C6892B' : CONTENT_GREEN;

  return (
    <section id="qualify" className="max-w-[1180px] mx-auto px-8 md:px-14 pt-16 pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
        <div className="lg:col-span-7">
          <div className="text-[11px] font-semibold tracking-wide mb-6" style={{ color: accent }}>
            자격 실시간 판정
          </div>
          <h2
            className="font-logo-b text-3xl md:text-4xl leading-tight"
            style={{ color: CONTENT_INK, letterSpacing: '-0.04em' }}
          >
            30초면,<br />내 자격이 보인다.
          </h2>
        </div>
        <div className="lg:col-span-5 flex items-end">
          <p style={{ fontSize: '15px', lineHeight: 1.7, color: CONTENT_INK, opacity: 0.7 }}>
            세 가지만 고르면 국민내일배움카드 대상 여부와 예상 지원 트랙을 바로 판정합니다.
            전화번호는 필요 없습니다.
          </p>
        </div>
      </div>

      <div className="max-w-[860px]">
        <div className="rounded-[36px] p-6 md:p-8" style={{ border: `1px solid ${CONTENT_BORDER}`, background: '#fff' }}>
          <div className="flex items-center justify-between gap-3 mb-4">
            <span className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase" style={{ color: CONTENT_MUTED }}>
              <i className="ri-flashlight-line text-[14px]" style={{ color: accent }} />
              자격 30초 즉시 판정기
            </span>
            <span className="text-[11px] font-semibold" style={{ color: accent }}>
              {done ? '완료' : `${Math.min(step + 1, total)} / ${total}`}
            </span>
          </div>

          <div className="flex gap-1.5 mb-7">
            {QUESTIONS.map((q, idx) => (
              <span
                key={q.id}
                className="h-[3px] flex-1 rounded-full transition-all duration-300"
                style={{ background: step > idx ? accent : 'rgba(20,18,16,0.10)' }}
              />
            ))}
          </div>

          {!done && current && (
            <div key={current.id} className="content-pop">
              <h3 className="font-logo-b text-[26px] md:text-[30px] leading-tight" style={{ color: CONTENT_INK, letterSpacing: '-0.02em' }}>
                {current.title}
              </h3>
              <p className="mt-2 text-[13px]" style={{ color: CONTENT_MUTED }}>
                {current.helper}
              </p>

              <div className={`mt-6 grid gap-3 ${current.options.length === 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2'}`}>
                {current.options.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => handleSelect(current.id, o.value)}
                    className="group rounded-2xl p-4 text-left cursor-pointer transition-all duration-200 hover:-translate-y-0.5"
                    style={{ border: `1px solid ${CONTENT_BORDER}`, background: `${accent}08` }}
                  >
                    <i className={`${o.icon} text-[20px] transition-colors`} style={{ color: CONTENT_MUTED }} />
                    <div className="mt-3 text-[13px] font-semibold" style={{ color: CONTENT_INK }}>
                      {o.label}
                    </div>
                    <div className="text-[10.5px] mt-0.5" style={{ color: CONTENT_MUTED }}>
                      {o.sub}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {done && verdict && (
            <div key="verdict" className="content-pop rounded-[28px] p-6" style={{ border: `1px solid ${verdict.tone === 'amber' ? 'rgba(198,137,43,0.35)' : 'rgba(62,142,90,0.32)'}`, background: verdict.tone === 'amber' ? 'rgba(198,137,43,0.06)' : 'rgba(62,142,90,0.06)' }}>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold px-3 h-[24px] rounded-full" style={{ color: toneColor, background: '#fff', border: `1px solid ${toneColor}55` }}>
                <i className={verdict.tone === 'amber' ? 'ri-error-warning-line' : 'ri-checkbox-circle-fill'} />
                {verdict.badge}
              </span>

              <h3 className="mt-4 font-logo-b text-[26px] md:text-[30px] leading-tight" style={{ color: CONTENT_INK, letterSpacing: '-0.02em' }}>
                {verdict.headline}
              </h3>
              <p className="mt-2 text-[13.5px] leading-relaxed" style={{ color: CONTENT_INK, opacity: 0.8 }}>
                {verdict.desc}
              </p>

              <div className="mt-3 flex items-start gap-1.5 text-[11.5px]" style={{ color: CONTENT_MUTED }}>
                <i className="ri-information-line mt-0.5" style={{ color: accent }} />
                <span>{verdict.note}</span>
              </div>

              <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
                <button
                  type="button"
                  onClick={handleAttach}
                  className="flex-1 h-[46px] rounded-full text-[11px] font-semibold text-white whitespace-nowrap cursor-pointer transition-all flex items-center justify-center gap-2"
                  style={{ background: accent }}
                >
                  <i className="ri-attachment-2" />
                  상담 신청서에 판정 결과 첨부
                </button>
                <button
                  type="button"
                  onClick={reset}
                  className="h-[46px] px-5 rounded-full text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-all flex items-center justify-center gap-1.5"
                  style={{ border: `1px solid ${CONTENT_BORDER}`, color: CONTENT_INK, background: '#fff' }}
                >
                  <i className="ri-refresh-line" />
                  다시 하기
                </button>
              </div>
              {attached && (
                <p className="mt-3 flex items-center gap-1.5 text-[10.5px] font-semibold" style={{ color: CONTENT_GREEN }}>
                  <i className="ri-checkbox-circle-fill" />
                  판정 결과가 상담 신청서 문의내용에 첨부되었습니다
                </p>
              )}
            </div>
          )}

          {!done && (
            <div className="mt-6 flex items-center gap-4 text-[11px]" style={{ color: CONTENT_MUTED }}>
              <span className="flex items-center gap-1"><i className="ri-shield-check-line" style={{ color: CONTENT_GREEN }} />전화번호 불필요</span>
              <span className="flex items-center gap-1"><i className="ri-lock-2-line" />익명 판정</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}